import { getByPathOrDefault } from "../../../services/common/object-util";
import FootballStringifier from "../../../services/stringifiers/football-stringifier";
import { statPreprocessor } from "./stat-preprocessor";
import SportCodes from "../../common/sport-codes";
import BoxScoreCalculator from "./box-score-calculator";
import Ruleset from "../enums/ruleset";

export default class BoxScore {
    constructor(
        gameContext = {},
        game = {},
        teamsStats = {},
        driveStats = [],
        homeTeam,
        awayTeam,
        event = {}
    ) {
        const [homeTeamStats, awayTeamStats] = Object.values(teamsStats);
        this.awayTeamStats = awayTeamStats.toJSON();
        this.homeTeamStats = homeTeamStats.toJSON();
        this.gameContext = gameContext;
        this.statistics = [];
        this.scoringSummary = [];
        this.lineScore = [];
        this.game = game;
        this.driveStats = driveStats;
        this.homeTeam = homeTeam;
        this.awayTeam = awayTeam;
        this.event = event;
    }

    calculate(ruleSetId) {
        const flagfballStats = [
            {
                label: "Offense",
                data: this.getOffenseStatistics()
            },
            {
                label: "Passing",
                data: this.getPassingStatistics()
            },
            {
                label: "Rushing",
                data: this.getRushingStatistics()
            },
            {
                label: "Down",
                data: this.getFirstDownStatistics()
            },
            {
                label: "Penalty",
                data: this.getPenaltyStatistics()
            },
            {
                label: "Punt",
                data: this.getPuntStatistics()
            },
            {
                label: "Punt Return",
                data: this.getReturnStatistics()
            },
            {
                label: "Turnover",
                data: this.getTurnoverStatistics()
            },
            {
                label: "Miscellaneous",
                data: this.getMiscStatistics()
            }
        ];
        const fballStats = [
            {
                label: "Offense",
                data: this.getOffenseStatistics(ruleSetId)
            },
            {
                label: "Passing",
                data: this.getPassingStatistics()
            },
            {
                label: "Rushing",
                data: this.getRushingStatistics()
            },
            {
                label: "Down",
                data: this.getFirstDownStatistics()
            },
            {
                label: "Penalty",
                data: this.getPenaltyStatistics()
            },
            {
                label: "Punt",
                data: this.getPuntStatistics()
            },
            {
                label: "Punt Return",
                data: this.getReturnStatistics()
            },
            {
                label: "Kickoff",
                data: this.getKickoffStatistics()
            },
            {
                label: "Kickoff Return",
                data: this.getKickoffReturnStatistics()
            },
            {
                label: "Kicking",
                data: this.getFieldGoalStatistics()
            },
            {
                label: "Turnover",
                data: this.getTurnoverStatistics(ruleSetId)
            },
            {
                label: "Miscellaneous",
                data: this.getMiscStatistics(ruleSetId)
            }
        ];
        this.statistics =
            this.game?.sportCode === SportCodes.Football
                ? fballStats
                : flagfballStats;
        this.driveSummary = this.getDriveSummary();
        this.lineScore = this.getLineScore();
    }

    getTotalTimeOfPossession(label, driveStats) {
        const totalDriveTimes = driveStats.reduce(
            (acc, driveStat) => {
                const { drive } = driveStat;
                const { homeTeamHasPossession } = drive;
                const currentTeamId = homeTeamHasPossession
                    ? this.homeTeam.id
                    : this.awayTeam.id;

                acc[currentTeamId] += drive.totalTime || 0;

                return acc;
            },
            {
                [this.awayTeam.id]: 0,
                [this.homeTeam.id]: 0
            }
        );

        return {
            name: label,
            away: FootballStringifier.milisecondsToMMSS(
                totalDriveTimes[this.awayTeam.id]
            ),
            home: FootballStringifier.milisecondsToMMSS(
                totalDriveTimes[this.homeTeam.id]
            )
        };
    }

    getOffenseStatistics(ruleSetId) {
        let statLineItems = [
            this.getStatLineItem(
                "Total Offense Yards",
                "stats.offense.total.totalYards"
            ),
            this.getStatLineItem(
                "Total Offense Plays",
                (stats) =>
                    `${
                        getByPathOrDefault(
                            stats,
                            "stats.offense.total.attempted",
                            0
                        ) +
                        getByPathOrDefault(
                            stats,
                            "stats.offense.kneelDown.attempted",
                            0
                        )
                    }`
            ),
            this.getStatLineItem("Offense Average Yards", (stats) => {
                return getByPathOrDefault(
                    stats,
                    "stats.offense.total.averageYards",
                    0
                ).toFixed(2);
            }),
            this.getStatLineItem(
                "Offense Largest Gain",
                "stats.offense.total.longestYard"
            ),
            this.getStatLineItem("Offensive Plays for Gain", (stat) => {
                return `${getByPathOrDefault(
                    stat,
                    "stats.offense.total.gainedPlays",
                    0
                )} - ${getByPathOrDefault(
                    stat,
                    "stats.offense.total.gainedYards",
                    0
                )}`;
            }),
            this.getTotalTimeOfPossession(
                "Total Time Of Possession",
                this.driveStats
            )
        ];

        if (ruleSetId === Ruleset.Cfl.code) {
            statLineItems = [
                this.getStatLineItem("Net Offense Yards", (stats) => {
                    const totalOffensiveYards = getByPathOrDefault(
                        stats,
                        "stats.offense.total.totalYards",
                        0
                    );
                    // We subtract sack yards because they already exist as part of the total offense yards
                    const totalTeamLoss =
                        getByPathOrDefault(
                            stats,
                            "stats.misc.teamLoss.totalYards",
                            0
                        ) -
                        Math.abs(
                            getByPathOrDefault(
                                stats,
                                "stats.offense.sack.totalYards",
                                0
                            )
                        );
                    return totalOffensiveYards - totalTeamLoss;
                })
            ].concat(statLineItems);
        }

        return statLineItems;
    }

    getTurnoverStatistics(ruleSetId) {
        let statLineItems = [
            this.getStatLineItem(
                "Fumbles lost",
                "stats.offense.fumble.lost.completed"
            ),
            this.getStatLineItem(
                "Interceptions",
                "stats.defense.interceptions.completed"
            ),
            this.getStatLineItem(
                "Fumble return yards",
                "stats.defense.fumble.returned.gainedYards"
            ),
            this.getStatLineItem(
                "Interception return yards",
                "stats.defense.interceptionReturns.gainedYards"
            ),
            this.getStatLineItem("Safeties", "stats.defense.safety.completed")
        ];

        if (ruleSetId === Ruleset.Cfl.code) {
            statLineItems = statLineItems.concat([
                this.getStatLineItem(
                    "On Downs",
                    "stats.offense.turnovers.onDown.completed"
                ),
                this.getStatLineItem(
                    "On Downs Redzone",
                    "stats.offense.turnovers.onDownRedzone.completed"
                )
            ]);
        }

        return statLineItems;
    }

    getMiscStatistics(ruleSetId) {
        let statLineItems = [
            this.getStatLineItem(
                "Total Yards",
                (stats) =>
                    getByPathOrDefault(
                        stats,
                        "stats.misc.continue.totalYards",
                        0
                    ) +
                    getByPathOrDefault(
                        stats,
                        "stats.misc.lateral.totalYards",
                        0
                    ) +
                    getByPathOrDefault(
                        stats,
                        "stats.misc.fgReturn.totalYards",
                        0
                    )
            )
        ];

        if (ruleSetId === Ruleset.Cfl.code) {
            statLineItems = statLineItems.concat([
                this.getStatLineItem("Team Loss", (stats) =>
                    getByPathOrDefault(
                        stats,
                        "stats.misc.teamLoss.totalYards",
                        0
                    )
                )
            ]);
        }

        return statLineItems;
    }

    getRedzoneStatistics() {
        return [
            this.getStatLineItem(
                "Points in Redzone",
                "stats.offense.redzone.points"
            ),
            this.getStatLineItem(
                "Redzone TD's",
                "stats.offense.redzone.touchdowns"
            )
        ];
    }

    getDownEfficiency(stat, down) {
        const dStatsRush = getByPathOrDefault(
            stat,
            `stats.offense.downs.rush.${down}`,
            0
        );
        const dStatsPass = getByPathOrDefault(
            stat,
            `stats.offense.downs.pass.${down}`,
            0
        );

        const totalCompleted =
            getByPathOrDefault(dStatsRush, "completed", 0) +
            getByPathOrDefault(dStatsPass, "completed", 0);

        const totalTouchDowns =
            getByPathOrDefault(dStatsRush, "touchdowns", 0) +
            getByPathOrDefault(dStatsPass, "touchdowns", 0);

        const totalAttempts =
            getByPathOrDefault(dStatsRush, "attempted", 0) +
            getByPathOrDefault(dStatsPass, "attempted", 0);

        const efficiency = totalAttempts
            ? (totalCompleted + totalTouchDowns) / totalAttempts
            : 0;

        return `${efficiency.toFixed(2)}`;
    }

    getFirstDownStatistics() {
        const {
            passing: homePassingFirstDowns,
            rushing: homeRushingFirstDowns,
            penalty: homePenaltyFirstDowns
        } = BoxScoreCalculator.getCombinedDownStats(
            this.homeTeamStats,
            this.gameContext.gameConfig.general.downs
        );

        const {
            passing: awayPassingFirstDowns,
            rushing: awayRushingFirstDowns,
            penalty: awayPenaltyFirstDowns
        } = BoxScoreCalculator.getCombinedDownStats(
            this.awayTeamStats,
            this.gameContext.gameConfig.general.downs
        );

        return [
            {
                name: "First Downs",
                away:
                    awayPassingFirstDowns +
                    awayRushingFirstDowns +
                    awayPenaltyFirstDowns,
                home:
                    homePassingFirstDowns +
                    homeRushingFirstDowns +
                    homePenaltyFirstDowns
            },
            {
                name: "Passing First Downs",
                away: awayPassingFirstDowns,
                home: homePassingFirstDowns
            },
            {
                name: "Rushing First Downs",
                away: awayRushingFirstDowns,
                home: homeRushingFirstDowns
            },
            {
                name: "Penalty",
                away: awayPenaltyFirstDowns,
                home: homePenaltyFirstDowns
            },
            this.getStatLineItem("Third Down Efficiency", (stat) =>
                this.getDownEfficiency(stat, 3)
            ),
            this.getStatLineItem("Fourth Down Efficiency", (stat) =>
                this.getDownEfficiency(stat, 4)
            )
        ];
    }

    getPassingStatistics() {
        return [
            this.getStatLineItem(
                "Passing Yards",
                "stats.offense.pass.totalYards"
            ),
            this.getStatLineItem("Completions", "stats.offense.pass.completed"),
            this.getStatLineItem("Attempts", "stats.offense.pass.attempted"),
            this.getStatLineItem("Completion Percentage", (stat) => {
                const pct = (
                    getByPathOrDefault(stat, "stats.offense.pass.percent", 0) *
                    100
                ).toFixed(2);

                return `${pct}%`;
            }),
            this.getStatLineItem(
                "Average Yards Per Pass",
                "stats.offense.pass.averageYards",
                0,
                2
            ),
            this.getStatLineItem(
                "Largest Gain on a Pass",
                "stats.offense.pass.longestYard"
            ),
            this.getStatLineItem("Passes for a Gain - Yards", (stat) => {
                return `${getByPathOrDefault(
                    stat,
                    "stats.offense.pass.gainedPlays",
                    0
                )} - ${getByPathOrDefault(
                    stat,
                    "stats.offense.pass.gainedYards",
                    0
                )}`;
            }),
            this.getStatLineItem("Sacked: Number - Yards", (stat) => {
                const { completed, totalYards } =
                    BoxScoreCalculator.getPassSacked(stat);
                return `${completed} - ${totalYards}`;
            }),
            this.getStatLineItem(
                "Intercepted",
                "stats.offense.pass.interceptions"
            )
        ];
    }

    getRushingStatistics() {
        return [
            this.getStatLineItem(
                "Rushing Yards",
                "stats.offense.rush.totalYards"
            ),
            this.getStatLineItem(
                "Attempts",
                (stats) =>
                    getByPathOrDefault(
                        stats,
                        "stats.offense.rush.completed",
                        0
                    ) +
                    getByPathOrDefault(
                        stats,
                        "stats.offense.kneelDown.completed",
                        0
                    )
            ),

            this.getStatLineItem("Average Yards Per Rush", (stats) => {
                return `${getByPathOrDefault(
                    stats,
                    "stats.offense.rush.averageYards",
                    0
                ).toFixed(2)}`;
            }),
            this.getStatLineItem(
                "Largest Gain on a Rush",
                "stats.offense.rush.longestYard"
            ),
            this.getStatLineItem("Rushes for a Gain - Yards", (stat) => {
                return `${getByPathOrDefault(
                    stat,
                    "stats.offense.rush.gainedPlays",
                    0
                )} - ${getByPathOrDefault(
                    stat,
                    "stats.offense.rush.gainedYards",
                    0
                )}`;
            }),
            this.getStatLineItem("Rushes for a Loss - Yards", (stat) => {
                return `${getByPathOrDefault(
                    stat,
                    "stats.offense.rush.lostPlays",
                    0
                )} - ${
                    getByPathOrDefault(
                        stat,
                        "stats.offense.rush.lostYards",
                        0
                    ) * -1
                }`;
            })
        ];
    }

    getReturnStatistics() {
        return [
            this.getStatLineItem("Total Number", (stats) => {
                return `${getByPathOrDefault(
                    stats,
                    "stats.specialTeams.puntReturn.completed",
                    0
                )}`;
            }),
            this.getStatLineItem("Yards", (stats) => {
                return `${getByPathOrDefault(
                    stats,
                    "stats.specialTeams.puntReturn.totalYards",
                    0
                )}`;
            }),
            this.getStatLineItem("Average Yards", (stats) => {
                return `${getByPathOrDefault(
                    stats,
                    "stats.specialTeams.puntReturn.averageYards",
                    0
                )}`;
            }),
            this.getStatLineItem(
                "Interception Returns # - Yards",
                (stats) => {
                    return `${getByPathOrDefault(
                        stats,
                        "stats.defense.interceptionReturn.completed",
                        0
                    )} - ${getByPathOrDefault(
                        stats,
                        "stats.defense.interceptionReturn.totalYards",
                        0
                    )}`;
                },
                null,
                null,
                true
            )
        ];
    }

    getPenaltyStatistics() {
        const { penalty: homePenaltyFirstDowns } =
            BoxScoreCalculator.getCombinedDownStats(
                this.homeTeamStats,
                this.gameContext.gameConfig.general.downs
            );

        const { penalty: awayPenaltyFirstDowns } =
            BoxScoreCalculator.getCombinedDownStats(
                this.awayTeamStats,
                this.gameContext.gameConfig.general.downs
            );
        return [
            this.getStatLineItem("Total Number", "stats.penalty.completed"),
            this.getStatLineItem("Total Yards", "stats.penalty.absoluteYards"),
            {
                name: "First Downs",
                away: awayPenaltyFirstDowns,
                home: homePenaltyFirstDowns
            }
        ];
    }

    getPuntStatistics() {
        return [
            this.getStatLineItem(
                "Total Number",
                "stats.specialTeams.punt.completed"
            ),
            this.getStatLineItem(
                "Average yards",
                "stats.specialTeams.punt.averageYards"
            ),
            this.getStatLineItem(
                "Touchbacks",
                "stats.specialTeams.punt.touchback"
            ),
            this.getStatLineItem("Rouge", "stats.specialTeams.punt.rouge")
        ];
    }

    getKickoffReturnStatistics() {
        return [
            this.getStatLineItem(
                "Total Number",
                "stats.kicks.kickoffReturn.completed"
            ),
            this.getStatLineItem(
                "Yards",
                "stats.kicks.kickoffReturn.totalYards"
            ),
            this.getStatLineItem(
                "Average Yards",
                "stats.kicks.kickoffReturn.averageYards"
            ),
            this.getStatLineItem(
                "Touchdowns",
                "stats.kicks.kickoffReturn.touchdowns"
            )
        ];
    }

    getKickoffStatistics() {
        return [
            this.getStatLineItem(
                "Total Number",
                "stats.kicks.kickoff.completed"
            ),
            this.getStatLineItem("Yards", "stats.kicks.kickoff.totalYards"),
            this.getStatLineItem(
                "Average Yards",
                "stats.kicks.kickoff.averageYards"
            ),
            this.getStatLineItem("Touchbacks", "stats.kicks.kickoff.touchback"),
            this.getStatLineItem("Rouge", "stats.kicks.kickoff.rouge")
        ];
    }

    getFieldGoalStatistics() {
        return [
            this.getStatLineItem("Good - Attempts", (stat) => {
                return `${getByPathOrDefault(
                    stat,
                    "stats.kicks.fieldGoal.completed",
                    0
                )} - ${getByPathOrDefault(
                    stat,
                    "stats.kicks.fieldGoal.attempted",
                    0
                )}`;
            }),
            this.getStatLineItem("RZ Good - RZ Attempts", (stat) => {
                return `${getByPathOrDefault(
                    stat,
                    "stats.kicks.fieldGoalRedzone.completed",
                    0
                )} - ${getByPathOrDefault(
                    stat,
                    "stats.kicks.fieldGoalRedzone.attempted",
                    0
                )}`;
            }),
            this.getStatLineItem("XP Good - XP Attempts", (stat) => {
                return `${getByPathOrDefault(
                    stat,
                    "stats.kicks.patKick.completed",
                    0
                )} - ${getByPathOrDefault(
                    stat,
                    "stats.kicks.patKick.attempted",
                    0
                )}`;
            }),
            this.getStatLineItem("Rouge", "stats.kicks.fieldGoal.rouge")
        ];
    }

    getDriveSummaryText(driveStat) {
        const totalPlays = driveStat.drive.plays.length;
        const totalYards = getByPathOrDefault(
            driveStat,
            "stats.offense.total.totalYards",
            0
        );

        const messages = [`Drive: ${totalPlays} plays`];
        if (driveStat.drive.completed) {
            const { totalTime } = driveStat.drive;
            messages.push(
                `${totalYards} Yards in ${FootballStringifier.milisecondsToMMSS(
                    totalTime
                )}`
            );
        } else {
            messages.push("in progress");
        }

        return messages.join(", ");
    }

    getDrivePlaysText(driveStat) {
        const messages = driveStat.drive.plays.map((p) => {
            const { title, messages } = FootballStringifier.summarize(
                p,
                this.gameContext
            );
            messages.unshift(title);
            return messages.join(" ");
        });

        return messages;
    }

    getDriveSummary() {
        let homeTotal = 0;
        let visitorTotal = 0;
        const scoringSummaryItems = [];

        this.driveStats.forEach((driveStat) => {
            const { drive, homeScore, awayScore } = driveStat;
            const driveSummary = this.getDriveSummaryText(driveStat);
            const drivePlaysText = this.getDrivePlaysText(driveStat);
            const scoreChanged = homeScore > 0 || awayScore > 0;

            homeTotal += homeScore;
            visitorTotal += awayScore;

            scoringSummaryItems.push({
                id: drive.id,
                period: drive.startPeriod,
                startTime: drive.startTime,
                homeTeamHasPossession: drive.homeTeamHasPossession,
                summary: driveSummary,
                playMessages: drivePlaysText,
                awayScore: visitorTotal,
                homeScore: homeTotal,
                totalYards: drive.totalYards,
                scoreChanged: scoreChanged
            });
        });

        return scoringSummaryItems;
    }

    getTotalTouchdowns(periodStats) {
        return getByPathOrDefault(
            periodStats,
            "stats.offense.touchdowns.completed",
            0
        );
    }

    getPatPoints(periodStats) {
        return getByPathOrDefault(
            periodStats,
            "stats.offense.pat.total.points",
            0
        );
    }

    getFieldGoalPoints(periodStats) {
        const totalCompleted = getByPathOrDefault(
            periodStats,
            "stats.kicks.fieldGoal.completed",
            0
        );
        // dropKick counts attempts (made or missed); only made drop kicks
        // score, so use the completed count or a missed drop kick would add
        // (dropKickFieldGoal - fieldGoal) phantom points to the period.
        const dropKickCompleted = getByPathOrDefault(
            periodStats,
            "stats.kicks.fieldGoal.dropKickCompleted",
            0
        );
        const regularCompleted = totalCompleted - dropKickCompleted;

        const regularPoints =
            regularCompleted * this.gameContext.gameConfig.scoring.fieldGoal;
        const dropKickPoints =
            dropKickCompleted *
            (this.gameContext.gameConfig.scoring.dropKickFieldGoal ||
                this.gameContext.gameConfig.scoring.fieldGoal);

        return regularPoints + dropKickPoints;
    }

    getSafetyPoints(periodStats) {
        const totalSafeties = getByPathOrDefault(
            periodStats,
            "stats.defense.safety.completed",
            0
        );
        return totalSafeties * this.gameContext.gameConfig.scoring.safety;
    }

    getUpdateFieldPoints(periodStats) {
        return getByPathOrDefault(
            periodStats,
            "stats.misc.updateField.points",
            0
        );
    }

    getDeucePoints(periodStats) {
        return (
            getByPathOrDefault(periodStats, "stats.kicks.deuce.completed", 0) *
            (this.gameContext.gameConfig.scoring.deuce || 0)
        );
    }

    getRougePoints(periodStats) {
        let totalRouge = 0;
        const rougePaths = [
            "specialTeams.punt.rouge",
            "kicks.kickoff.rouge",
            "kicks.fieldGoal.rouge"
        ];

        rougePaths.forEach((path) => {
            totalRouge += getByPathOrDefault(periodStats, `stats.${path}`, 0);
        });

        return totalRouge * this.gameContext.gameConfig.scoring.rougePoints;
    }

    getScoreByPeriod(teamStats, period) {
        const { periods } = teamStats;
        const periodStats = periods.length ? periods[period - 1] : {};

        const totalTouchdowns = this.getTotalTouchdowns(periodStats);
        const touchdownPoints =
            totalTouchdowns * this.gameContext.gameConfig.scoring.touchdown;
        const patPoints = this.getPatPoints(periodStats);
        const rougePoints = this.getRougePoints(periodStats);
        const fieldGoalPoints = this.getFieldGoalPoints(periodStats);
        const safetyPoints = this.getSafetyPoints(periodStats);
        const updateFieldPoints = this.getUpdateFieldPoints(periodStats);
        const deucePoints = this.getDeucePoints(periodStats);

        return (
            touchdownPoints +
            patPoints +
            rougePoints +
            fieldGoalPoints +
            safetyPoints +
            updateFieldPoints +
            deucePoints
        );
    }

    getLineScore() {
        const totalPeriods = Math.max(
            this.gameContext.currentPeriod,
            this.gameContext.gameConfig.general.periodsInRegulation
        );
        let totalHomeScore = 0;
        let totalAwayScore = 0;
        const scoreByPeriod = [];

        [...Array(totalPeriods).keys()].forEach((period) => {
            const currentPeriod = period + 1;
            if (currentPeriod <= this.gameContext.currentPeriod) {
                const currentAwayScore = this.getScoreByPeriod(
                    this.awayTeamStats,
                    currentPeriod
                );
                const currentHomeScore = this.getScoreByPeriod(
                    this.homeTeamStats,
                    currentPeriod
                );

                scoreByPeriod.push({
                    number: currentPeriod,
                    awayScore: currentAwayScore,
                    homeScore: currentHomeScore
                });

                totalAwayScore += currentAwayScore;
                totalHomeScore += currentHomeScore;
            } else {
                scoreByPeriod.push({
                    number: currentPeriod,
                    awayScore: 0,
                    homeScore: 0
                });
            }
        });

        const lineScore = [
            ...scoreByPeriod,
            {
                number: "Total",
                awayScore: totalAwayScore,
                homeScore: totalHomeScore,
                isTotal: true
            }
        ];

        return lineScore;
    }

    getStatLineItem(
        label,
        field,
        defaults = 0,
        fractionDigits,
        hidden = false
    ) {
        const fn =
            typeof field === "string"
                ? (stats) => {
                      let value = getByPathOrDefault(stats, field, defaults);
                      if (fractionDigits) {
                          value = value.toFixed(fractionDigits);
                      }

                      return `${statPreprocessor[field]?.(value) || value}`;
                  }
                : field;

        return {
            name: label,
            away: fn(this.awayTeamStats),
            home: fn(this.homeTeamStats),
            hidden: hidden
        };
    }
}
